import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Trophy } from "lucide-react";
import AchievementBadge from "./AchievementBadge";
import T from "@/i18n/T";

interface AchievementsModalProps {
  isOpen: boolean;
  onClose: () => void;
  allAchievements: any[];
  userAchievements: any[];
}

const AchievementsModal = ({
  isOpen,
  onClose,
  allAchievements,
  userAchievements,
}: AchievementsModalProps) => {
  const unlockedCount = userAchievements.length;
  const totalCount = allAchievements.length;
  const progressPercentage = totalCount > 0 ? (unlockedCount / totalCount) * 100 : 0;

  const unlockedIds = new Set(userAchievements.map((ua: any) => ua.achievement_id));

  const categories = Array.from(
    new Set(allAchievements.map((a) => a.category))
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="h-6 w-6 text-rank-gold" />
            <T k="auto.learning-achievements-2068" />
          </DialogTitle>
          <DialogDescription>
            <T k="auto.track-your-learning-progress-and-unlock-7404" />
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Overall progress */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="font-medium"><T k="auto.overall-progress-e9d5" /></span>
              <span className="text-muted-foreground">
                {unlockedCount} / {totalCount} unlocked
              </span>
            </div>
            <Progress value={progressPercentage} className="h-2" />
          </div>

          {/* Achievements by category */}
          <Tabs defaultValue="all" className="w-full">
            <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${categories.length + 1}, 1fr)` }}>
              <TabsTrigger value="all">All</TabsTrigger>
              {categories.map((category) => (
                <TabsTrigger key={category} value={category}>
                  {category}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="all" className="space-y-4 mt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {allAchievements.map((achievement) => {
                  const userAchievement = userAchievements.find(
                    (ua: any) => ua.achievement_id === achievement.id
                  );
                  return (
                    <AchievementBadge
                      key={achievement.id}
                      achievement={{
                        ...achievement,
                        unlocked_at: userAchievement?.unlocked_at,
                      }}
                      isUnlocked={unlockedIds.has(achievement.id)}
                    />
                  );
                })}
              </div>
            </TabsContent>

            {categories.map((category) => (
              <TabsContent key={category} value={category} className="space-y-4 mt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {allAchievements
                    .filter((a) => a.category === category)
                    .map((achievement) => {
                      const userAchievement = userAchievements.find(
                        (ua: any) => ua.achievement_id === achievement.id
                      );
                      return (
                        <AchievementBadge
                          key={achievement.id}
                          achievement={{
                            ...achievement,
                            unlocked_at: userAchievement?.unlocked_at,
                          }}
                          isUnlocked={unlockedIds.has(achievement.id)}
                        />
                      );
                    })}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AchievementsModal;
